import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getPatients, selectPatients } from '@/redux/reducers/patient.reducer'
import { getStaffs, selectStaffs } from '@/redux/reducers/staff.reducer'
import { getExpertises, selectExpertiseSearchParams } from '../../../redux/reducers/expertise.reducer'
import { ExpertisesSearchFormProps, ExpertisesSearchFormValues } from '../components/ExpertisesSearchForm'
import ExpertisesSearchFormMobile from '../components/ExpertisesSearchFormMobile'

export type ExpertisesSearchFormMobileContainerProps = Pick<ExpertisesSearchFormProps, 'direction'>

export function ExpertisesSearchFormMobileContainer(props: ExpertisesSearchFormMobileContainerProps) {
  const dispatch = useAppDispatch()

  const expertiseSearchParams = useAppSelector(selectExpertiseSearchParams)
  const patients = useAppSelector(selectPatients)
  const staffs = useAppSelector(selectStaffs)

  const doctorOptions = useMemo(
    () => _.map(_.filter(staffs, ['status', 'MEDECIN']), (doctor) => ({ label: `${doctor.userInfo.name} ${doctor.userInfo.surname}`, value: doctor._id })),
    [staffs]
  )

  const patientOptions = useMemo(
    () =>
      _.map(patients, (patient) => ({
        label: `${patient.userInfo.name} ${patient.userInfo.surname}`,
        value: patient._id,
      })),
    [patients]
  )

  const transformData = (values: ExpertisesSearchFormValues) => {
    return {
      medecin: values.doctor || undefined,
      patient: values.patient || undefined,
      type: values.type || undefined,
      startDate: {
        $gte: values.startDate.$gte ? dayjs(values.startDate.$gte).startOf('day').toISOString() : undefined,
        $lte: values.startDate.$lte ? dayjs(values.startDate.$lte).endOf('day').toISOString() : undefined,
      },
    }
  }

  const handleSearchExpertises = (values: ExpertisesSearchFormValues) => {
    const data = transformData(values)
    dispatch(getExpertises(data, { limit: expertiseSearchParams?.options?.limit }))
  }

  const resetSearch = () => {
    dispatch(getExpertises({}, { limit: 5 }))
  }

  const handleSearchDoctor = useDebouncedCallback((value: string) => {
    const [name, surname] = value.split(' ')
    dispatch(getStaffs({ name, surname }))
  }, 300)

  const handleSearchPatient = useDebouncedCallback((value: string) => {
    dispatch(getPatients({ smartSearch: value || undefined }))
  }, 300)

  return (
    <ExpertisesSearchFormMobile
      {...props}
      doctorOptions={doctorOptions}
      onReset={resetSearch}
      onSearchDoctor={handleSearchDoctor}
      onSearchPatient={handleSearchPatient}
      onSubmit={handleSearchExpertises}
      patientOptions={patientOptions}
    />
  )
}

export default ExpertisesSearchFormMobileContainer
